import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { setUser } from '../store/authSlice'
import authService from '../services/authService'
import { useTranslation } from '../hooks/use-i18n'
import { consumeInviteRedirectPath, rememberInviteToken } from '../utils/inviteRedirect'
import { AuthGoogleSection } from '../components/auth/GoogleSignInButton'

interface SignupForm {
  name: string
  email: string
  password: string
  confirmPassword: string
}

const Signup: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite') || ''
  const prefillEmail = searchParams.get('email') || ''
  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupForm>({
    defaultValues: { email: prefillEmail },
  })

  const password = watch('password')

  const onSubmit = async (data: SignupForm) => {
    try {
      setIsLoading(true)
      const response = await authService.signUp({
        name: data.name,
        email: data.email,
        password: data.password
      })
      authService.setAuthData(response.user, response.token)
      dispatch(setUser(response.user))
      toast.success(t('auth.accountCreatedToast'))
      rememberInviteToken(inviteToken)
      navigate(consumeInviteRedirectPath('/'))
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.signupFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center pt-12 sm:pt-16 lg:pt-20 pb-6 sm:pb-12 px-3 xs:px-4 overflow-x-hidden">
      <div className="max-w-md w-full min-w-0">
        <div className="glass-strong rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 xl:p-10">
          <div className="text-center mb-4 sm:mb-6 lg:mb-8">
            <h1 className="font-display text-lg sm:text-xl lg:text-display-2 font-bold mb-1.5 sm:mb-2 text-foreground">{t('auth.joinTitle')}</h1>
            <p className="text-small sm:text-body-lg text-foreground/90">{t('auth.joinSubtitle')}</p>
          </div>

          <form className="space-y-3 sm:space-y-4 lg:space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="name" className="text-small sm:text-nav font-medium mb-1.5 sm:mb-2 block text-foreground">{t('auth.fullName')}</label>
              <input
                {...register('name', { required: t('auth.nameRequired') })}
                type="text"
                className="w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-muted/20 border border-border text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-small sm:text-body"
                placeholder={t('auth.enterFullName')}
              />
              {errors.name && <p className="mt-1.5 text-small text-destructive">{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor="email" className="text-small sm:text-nav font-medium mb-1.5 sm:mb-2 block text-foreground">{t('auth.email')}</label>
              <input
                {...register('email', {
                  required: t('auth.emailRequired'),
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t('auth.emailInvalid') }
                })}
                type="email"
                className="w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-muted/20 border border-border text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-small sm:text-body"
                placeholder={t('auth.enterEmail')}
              />
              {errors.email && <p className="mt-1.5 text-small text-destructive">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="password" className="text-small sm:text-nav font-medium mb-1.5 sm:mb-2 block text-foreground">{t('auth.password')}</label>
              <input
                {...register('password', {
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.passwordMinLength')
                  }
                })}
                type="password"
                className="w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-muted/20 border border-border text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-small sm:text-body"
                placeholder={t('auth.enterPassword')}
              />
              {errors.password && <p className="mt-1.5 text-small text-destructive">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="text-small sm:text-nav font-medium mb-1.5 sm:mb-2 block text-foreground">{t('auth.confirmPassword')}</label>
              <input
                {...register('confirmPassword', {
                  required: t('auth.confirmPasswordRequired'),
                  validate: value => value === password || t('auth.passwordsDoNotMatch')
                })}
                type="password"
                className="w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-muted/20 border border-border text-foreground placeholder:text-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-small sm:text-body"
                placeholder={t('auth.confirmPasswordPlaceholder')}
              />
              {errors.confirmPassword && <p className="mt-1.5 text-small text-destructive">{errors.confirmPassword.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 sm:py-3 lg:py-3.5 rounded-lg sm:rounded-xl bg-primary text-primary-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all glow-subtle disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
            </button>
          </form>

          <AuthGoogleSection mode="sign-up" />

          <div className="mt-4 sm:mt-6 text-center">
            <p className="text-small sm:text-body-lg text-foreground/90">
              {t('auth.alreadyHaveAccount')} <Link to={`/login${inviteToken ? `?invite=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(prefillEmail)}` : ''}`} className="text-primary font-medium hover:underline">{t('auth.signInHere')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Signup
